import React, { useEffect, useState } from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  ListItemButton,
  Collapse,
  Theme,
  SxProps,
  Badge,
} from "@mui/material";
import { useTheme } from '@mui/material/styles';
import {
  ListAlt as ListAltIcon,
  LocalShipping as DeliveryIcon,
  Person as CustomerIcon,
  RestaurantMenu as RestaurantMenuIcon,
  // Inventory as ItemIcon,
  ExpandLess,
  ExpandMore,
} from "@mui/icons-material";
import { useLocation } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { COLORS } from "../../theme/themeConstants";
import { useForceNavigate } from "../HelperFunction/Hooks/forceNavigateWithQuery";
import { useOrderManagementApi } from '../../Hooks/useOrderManagementApi';
import { usePublicSettings } from '../../Hooks/usePublicSettings';

const drawerWidth = 300;

interface SidebarProps {
  open: boolean;
  onClose: () => void;
}

interface MenuItem {
  text: string;
  path: string;
  submenu?: boolean;
  reviewType?: 'Item' | 'Store' | 'DeliveryPerson';
}

interface MainMenuItem {
  key: string;
  text: string;
  icon: React.ReactElement;
  path: string;
  submenu?: boolean;
}

// Styles
const drawerStyles = (open: boolean, isRTL: boolean): SxProps<Theme> => ({
  width: open ? drawerWidth : 0,
  flexShrink: 0,
  position: "fixed",
  height: "100%",
  "& .MuiDrawer-paper": {
    width: drawerWidth,
    boxSizing: "border-box",
    backgroundColor: COLORS.sidebar?.main || COLORS.background.main,
    color: COLORS.secondary.light,
    ...(isRTL ? { borderLeft: "none" } : { borderRight: "none" }),
    height: "100%",
    transform: open
      ? "none"
      : isRTL
        ? `translateX(${drawerWidth}px)`
        : `translateX(-${drawerWidth}px)`,
    transition: "transform 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms",
    visibility: "visible !important",
    overflowX: "hidden",
    marginTop: "64px",
    paddingBottom: "64px",
    "&::-webkit-scrollbar": {
      width: "6px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "rgba(255, 255, 255, 0.2)",
      borderRadius: "3px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "transparent",
    },
  },
});

const listItemStyles = (isSelected: boolean): SxProps<Theme> => ({
  minHeight: 48,
  px: 2.5,
  backgroundColor: isSelected ? COLORS.primary.main : "transparent",
  color: isSelected ? "white" : COLORS.secondary.light,
  "&.Mui-selected": {
    backgroundColor: COLORS.primary.main,
    color: "white",
    "&:hover": {
      backgroundColor: COLORS.primary.main,
    },
    "& .MuiListItemIcon-root": {
      color: "white",
    },
  },
  "&:hover": {
    backgroundColor: isSelected ? COLORS.primary.dark : "rgba(255, 87, 34, 0.08)",
  },
});

console.log('listItemStyles:', listItemStyles);


const listItemStyless = (isSelected: boolean, isRTL: boolean): SxProps<Theme> => ({
  minHeight: 48,
  padding: isRTL ? "8px 20px" : "8px 20px 8px 56px",
  backgroundColor: isSelected ? COLORS.primary.main : "transparent",
  color: isSelected ? "white" : COLORS.secondary.light,
  display: 'flex',
  alignItems: 'center',
  justifyContent: isRTL ? 'center' : 'flex-start',
  "&.Mui-selected": {
    backgroundColor: COLORS.primary.main,
    color: COLORS.secondary.main,
    "&:hover": {
      backgroundColor: COLORS.primary.main,
    },
    "& .MuiListItemIcon-root": {
      color: "white",
    },
  },
  "&:hover": {
    backgroundColor: isSelected ? COLORS.primary.main : "rgba(255, 87, 34, 0.08)",
  },
});

const listItemIconStyles = (isSelected: boolean): SxProps<Theme> => ({
  minWidth: 40,
  color: isSelected ? "white" : COLORS.secondary.light,
});

const listItemTextStyles = (isRTL: boolean = false, depth: number = 0): SxProps<Theme> => ({
  "& .MuiTypography-root": {
    fontSize: "0.9rem",
    textAlign: isRTL ? 'center' : 'left',
    paddingLeft: isRTL && depth > 0 ? `${depth * 16}px` : '0px',
    paddingRight: !isRTL && depth > 0 ? `${depth * 16}px` : '0px',
    width: '100%',
    display: 'flex',
    justifyContent: isRTL ? 'center' : 'flex-start',
    alignItems: 'center',
    margin: '0 auto',
  },
  // Force center alignment for Arabic text
  ...(isRTL ? {
    textAlign: 'center !important',
    '& .MuiTypography-root': {
      textAlign: 'center !important',
      margin: '0 auto !important',
    }
  } : {} as SxProps<Theme>),
});

// Submenu items
const categoryManagement: MenuItem[] = [
  { text: "nav.manageItemCategory", path: "/item-category/list" },
];

const choiceGroupManagement: MenuItem[] = [
  { text: "nav.manageChoiceGroup", path: "/add-choice-group" },
  { text: "nav.manageChoiceGroup", path: "/manage-choice-group" },
];

const itemManagement: MenuItem[] = [
  { text: "nav.itemManagement", path: "/item-management" },
];

const reviewManagement: MenuItem[] = [
  { text: 'nav.reviewItem', path: '/layout/manage-item-review', reviewType: 'Item' },
  { text: 'nav.reviewRestaurant', path: '/layout/manage-restaurant-review', reviewType: 'Store' },
  // { text: 'nav.reviewDelivery', path: '/layout/manage-order-review', reviewType: 'DeliveryPerson' },
];

const reportsManagement: MenuItem[] = [
  { text: 'nav.salesReport', path: '/layout/reports/sales' },
  { text: 'nav.cancelledOrdersReport', path: '/layout/reports/cancelled-orders' },
  { text: 'nav.rejectedOrdersReport', path: '/layout/reports/rejected-orders' },
  { text: 'nav.pendingOrdersReport', path: '/layout/reports/pending-orders' },
  { text: 'nav.adminCommissionReport', path: '/layout/reports/admin-commission' },
  // Merchant payout and refund reports are not needed in merchant app UI
];

// Main menu with keys
const mainMenuItems: MainMenuItem[] = [
  {
    key: "dashboard",
    text: "nav.dashboard",
    icon: <ListAltIcon />,
    path: "/layout/dashboard",
    submenu: false,
  },
  // {
  //   key: "categoryManagement",
  //   text: "Category Management",
  //   icon: <ListAltIcon />,
  //   path: "/category-management",
  //   submenu: true,
  // },
  {
    key: "itemCategoryManagement",
    text: 'nav.manageItemCategory',
    icon: <ListAltIcon />,
    path: '/layout/manageItemCategory',
    submenu: false,
  },
  {
    key: "choiceGroupManagement",
    text: "nav.manageChoiceGroup",
    icon: <ListAltIcon />,
    path: "/layout/manage-choice-group",
    submenu: false,
  },
  {
    key: "manageChoices",
    text: "nav.manageChoices",
    icon: <ListAltIcon />,
    path: "/layout/manage-choice",
    submenu: false,
  },
  {
    key: "manageMeal",
    text: "nav.manageMeal", 
    icon: <RestaurantMenuIcon />,
    path: "/layout/manage-meal",
    submenu: false,
  },
  {
    key: "itemManagement",
    text: "nav.itemManagement",
    icon: <CustomerIcon />,
    path: "/layout/item-management",
    submenu: false,
  },
  {
    key: "reviewManagement",
    text: "nav.reviewManagement",
    icon: <ListAltIcon />,
    path: "/layout/review-management",
    submenu: true,
  },
  {
    key: "orderManagement",
    text: "nav.orderManagement",
    icon: <CustomerIcon />,
    path: "/layout/orderManagement",
    submenu: false,
  },
  // {
  //   key: "commissionTracking",
  //   text: "nav.commissionTracking",
  //   icon: <DeliveryIcon />,
  //   path: "/layout/commissionTracking",
  //   submenu: false,
  // },
  {
    key: "stockManagement",
    text: "nav.stockManagement",
    icon: <DeliveryIcon />,
    path: "/layout/stockManagement",
    submenu: false,
  },
  {
    key: "storeManagement",
    text: "nav.storeManagement",
    icon: <ListAltIcon />,
    path: "/layout/storeManagement",
    submenu: false,
  },
  // {
  //   key: "cancellationPayment",
  //   text: "nav.cancellationPayment",
  //   icon: <CustomerIcon />,
  //   path: "/layout/cancellation-payment",
  //   submenu: false,
  // },
  {
    key: "makeyourRestaurant",
    text: "nav.featuredRestaurant",
    icon: <CustomerIcon />,
    path: "/layout/makeyourRestaurant",
    submenu: false,
  },
  {
    key: "settlements",
    text: 'nav.settlements',
    icon: <DeliveryIcon />,
    path: '/layout/settlements',
    submenu: false,
  },
  {
    key: "reports",
    text: 'nav.reports',
    icon: <ListAltIcon />,
    path: '/layout/reports',
    submenu: true,
  },
];

// MenuItem component
const MenuItem: React.FC<{
  item: MenuItem & { badgeContent?: number };
  isSelected: boolean;
  submenu: boolean;
  onClick: () => void;
  depth?: number;
  isRTL: boolean;
}> = ({ item, isSelected, onClick, depth = 0, isRTL }) => {
  const { t } = useTranslation();
  const label = t(item.text);
  return (
  <ListItemButton
    onClick={onClick}
    selected={isSelected}
    sx={listItemStyless(isSelected, isRTL)}
    style={isRTL ? { paddingRight: depth * 16 + 16 } : { paddingLeft: depth * 16 + 16 }}
  >
    <ListItemIcon sx={{
      ...listItemIconStyles(isSelected),
      ...(isRTL ? { 
        minWidth: '40px',
        justifyContent: 'center',
        display: 'flex',
        position: 'absolute',
        left: '8px',
        zIndex: 1
      } : {})
    }} />
    {/* Render badge if badgeContent is provided */}
    {typeof item.badgeContent === 'number' ? (
      <Badge 
        color="error" 
        badgeContent={item.badgeContent} 
        invisible={item.badgeContent === 0}
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: isRTL ? 'center' : 'flex-start',
          alignItems: 'center',
        }}
      >
        <ListItemText
          primary={label}
          sx={{
            ...listItemTextStyles(isRTL, depth),
            // Additional center alignment for RTL
            ...(isRTL ? {
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center',
              '& .MuiTypography-root': {
                textAlign: 'center !important',
                margin: '0 auto !important',
              }
            } : {})
          }}
        />
      </Badge>
    ) : (
      <ListItemText
        primary={label}
        sx={{
          ...listItemTextStyles(isRTL, depth),
          // Additional center alignment for RTL
          ...(isRTL ? {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            '& .MuiTypography-root': {
              textAlign: 'center !important',
              margin: '0 auto !important',
            }
          } : {})
        }}
      />
    )}
  </ListItemButton>
);
}

// DropdownMenu component
const DropdownMenu: React.FC<{
  title: string;
  icon?: React.ReactElement;
  items: MenuItem[];
  isOpen: boolean;
  isPathActive: boolean;
  onToggle: () => void;
  currentPath: string;
  onItemClick: (item: MenuItem) => void; // <-- change from string to MenuItem
  isRTL: boolean;
}> = React.memo(
  ({
    title,
    items,
    isOpen,
    isPathActive,
    onToggle,
    currentPath,
    onItemClick,
    isRTL,
  }) => (
    <>
      <ListItem disablePadding>
        <ListItemButton
          onClick={onToggle}
          selected={isPathActive}
          sx={listItemStyless(isPathActive, isRTL)}
        >
          <TranslatedListItemText titleKey={title} isRTL={isRTL} />
          {isOpen ? <ExpandLess /> : <ExpandMore />}
        </ListItemButton>
      </ListItem>
      <Collapse in={isOpen} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {items.map((item) => (
            <MenuItem
              key={item.path}
              item={item}
              isSelected={currentPath === item.path}
              onClick={() => onItemClick(item)}
              depth={1}
              submenu={true}
              isRTL={isRTL}
            />
          ))}
        </List>
      </Collapse>
    </>
  )
);

// Helper component to translate dropdown titles
const TranslatedListItemText: React.FC<{ titleKey: string; isRTL?: boolean }> = ({ titleKey, isRTL = false }) => {
  const { t } = useTranslation();
  return (
    <ListItemText 
      primary={t(titleKey)} 
      sx={{
        ...listItemTextStyles(isRTL, 0),
        // Additional center alignment for RTL
        ...(isRTL ? {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
          '& .MuiTypography-root': {
            textAlign: 'center !important',
            margin: '0 auto !important',
          }
        } : {})
      }} 
    />
  );
};

// Sidebar Component
const Sidebar: React.FC<SidebarProps> = ({ open, onClose }) => {
  const location = useLocation();
  const theme = useTheme();
  const isRTL = theme.direction === 'rtl';

  // This maps paths to submenu items and their open state keys
  const submenuConfig: Record<string, MenuItem[]> = {
    '/layout/review-management': reviewManagement,
    '/layout/reports': reportsManagement,
    "/layout/category-management": categoryManagement,
    "/layout/manage-choice-group": choiceGroupManagement,
    "/layout/item-management": itemManagement,

  };

  // State for open/close for each submenu
  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({});

  const toggleMenu = (path: string) => {
    setOpenMenus((prev) => ({
      ...prev,
      [path]: !prev[path],
    }));
  };


  useEffect(() => {
    const newOpenStates: Record<string, boolean> = {};

    for (const item of mainMenuItems) {
      if (item.submenu) {
        // Check if any submenu item path matches current path
        const submenuItems = submenuConfig[item.path];
        const isActive = submenuItems?.some(sub => location.pathname.startsWith(sub.path));
        newOpenStates[item.path] = isActive;
      }
    }

    setOpenMenus(newOpenStates);
  }, [location.pathname]);

  const { forceNavigateWithQuery } = useForceNavigate();

  // Public Settings - for featured restaurant enabled check
  const { featuredRestaurantEnabled } = usePublicSettings();

  // New Orders Badge State
  const { getAllOrderManagement } = useOrderManagementApi();
  const [newOrdersCount, setNewOrdersCount] = useState<number>(0);

  // Fetch new orders count function
  const fetchNewOrdersCount = async () => {
    try {
      // Fetch only first page, just need count
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      const storeId = userData?.userDetails?.storeId || '';
      const merchantId = userData?.userId || '';
      const data = await getAllOrderManagement({
        page: 1,
        limit: 100,
        storeId,
        merchantId,
      });
      const count = Array.isArray(data?.orders)
        ? data.orders.filter((order: any) => order.status === 'Order Placed').length
        : 0;
      setNewOrdersCount(count);
    } catch (e) {
      setNewOrdersCount(0);
    }
  };

  // Poll every 30 seconds
  useEffect(() => {
    fetchNewOrdersCount();
    const interval = setInterval(fetchNewOrdersCount, 30000);
    return () => clearInterval(interval);
  }, []);

  // const isSettingsPath = location.pathname.startsWith('/settings');
  // const isSubAdminPath = location.pathname.startsWith('/sub-admin');
  // const isCategoriesPath = location.pathname.startsWith('/categories');

  // Filter menu items based on settings
  const filteredMenuItems = mainMenuItems.filter((item) => {
    // Hide featured restaurant menu item if disabled
    if (item.key === 'makeyourRestaurant' && !featuredRestaurantEnabled) {
      return false;
    }
    return true;
  });

  return (
    <Drawer variant="permanent" anchor={isRTL ? "right" : "left"} sx={drawerStyles(open, isRTL)} onClose={onClose}>
      <Box sx={{ overflow: 'auto', height: '100%' }}>
        <List>
          {filteredMenuItems.map((item) => {
            const isPathActive = location.pathname.startsWith(item.path);
            const isDropdownOpen = openMenus[item.path] || false;

            // Add badge to Order Management menu item
            if (item.key === 'orderManagement') {
              return (
                <MenuItem
                  key={item.path}
                  item={{ ...item, badgeContent: newOrdersCount }}
                  isSelected={location.pathname === item.path}
                  onClick={() => forceNavigateWithQuery(item.path)}
                  submenu={false}
                  isRTL={isRTL}
                />
              );
            }

            if (item.submenu) {
              const submenuItems = submenuConfig[item.path];
              return (
                <DropdownMenu
                  key={item.path}
                  title={item.text}
                  items={submenuItems}
                  isOpen={isDropdownOpen}
                  isPathActive={isPathActive}
                  onToggle={() => toggleMenu(item.path)}
                  currentPath={location.pathname}
                  onItemClick={(item) => forceNavigateWithQuery(item, true)}
                  isRTL={isRTL}
                />
              );
            }

            return (
              <MenuItem
                key={item.path}
                item={item}
                isSelected={location.pathname === item.path}
                onClick={() => forceNavigateWithQuery(item.path)}
                submenu={false}
                isRTL={isRTL}
              />
            );
          })}
        </List>
      </Box>
    </Drawer>
  );
};


export default Sidebar;
